import { Lenis } from '@studio-freight/react-lenis';
import { useQuery } from 'react-query';
import CustomLdJson from '@/components/global/CustomLdJson';
import ScrollToTopButton from '@/components/global/ScrollToTopButton';
import { PartnerLinks } from '@/components/shared/PartnerLinks/PartnerLinks';
import { IComponentGlobalFooter, IComponentSharedPartnerLink, IPageEntity, IStoryEntity } from '@/graphql/__generated__/global/types';
import { generateItemListElements } from '@/helpers/generateItemListElements';
import { getBaseUrl } from '@/helpers/getBaseUrl';
import { I18nLocales } from '@/interfaces/locales.interfaces';
import { fetchGlobalData } from '@/lib/strapiQueries';
import { BreadcrumbJsonLd, WebPageJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import { Toaster } from '../elements/Toast/Toast';
import { Footer as FooterComp } from './Footer';
import GridDebug from './GridDebug';
import { Header } from './Header';
import { PreviewBanner } from './PreviewBanner';
import Seo from './Seo';

export type LayoutProps = {
    children: React.ReactNode;
    page: IPageEntity | IStoryEntity;
    preview: boolean;
};

const baseUrl = getBaseUrl();

const Layout = ({ children, page, preview }: LayoutProps) => {
    const router = useRouter();
    const { data: global } = useQuery(`global-${router.locale}`, () => fetchGlobalData(router.locale as I18nLocales), { staleTime: Infinity });
    const { renderNavigation } = global;
    const { partnerLinks = [], footer } = global?.global?.data?.attributes ?? {};

    return (
        <>
            <Lenis root>
                <PreviewBanner preview={preview} />
                <GridDebug />
                <Seo page={page} />
                <Header navigation={renderNavigation} showRegionPopup={page.attributes?.showRegionPopup} />
                {children}
                <ScrollToTopButton />
                <FooterComp partners={<PartnerLinks partnerLinks={partnerLinks as IComponentSharedPartnerLink[]} />} footer={footer as IComponentGlobalFooter} />
                <Toaster />
            </Lenis>
            <WebPageJsonLd
                description={page.attributes?.seo?.metaDescription}
                id={`${baseUrl}${router.asPath}`}
                lastReviewed={page.attributes?.updatedAt}
                reviewedBy={{
                    type: 'Organization',
                    name: 'Star RV',
                }}
            />
            <BreadcrumbJsonLd itemListElements={generateItemListElements(page, [])} />
            <CustomLdJson customLdJson={page.attributes?.seo?.customLdJson} />
        </>
    );
};

export default Layout;
